import { CommonModule } from "@angular/common";
import { Component, inject, signal, AfterViewInit } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { AbortSequenceModal } from "@components/abort-sequence-modal/abort-sequence-modal";
import { AuthenticatingScreen } from "@components/authenticating-screen/authenticating-screen";
import { EducationLevelSelection } from "@components/education-level-selection/education-level-selection";
import { ErrorPage } from "@components/error-page/error-page";
import { Exercises } from "@components/exercises/exercises";
import { HomophoneGroupSelection } from "@components/homophone-groups-selection/homophone-groups-selection";
import { Modal } from "@components/modal/modal";
import { RewardCursorIndicationsModal } from "@components/reward-cursor-indications-modal/reward-cursor-indications-modal";
import { RewardMechanism } from "@components/reward-mechanism/reward-mechanism";
import { SequenceCompletionModal } from "@components/sequence-completion-modal/sequence-completion-modal";
import { SequenceEndScreen } from "@components/sequence-end-screen/sequence-end-screen";
import { WelcomeScreen } from "@components/welcome-screen/welcome-screen";
import { EducationLevelGroup } from "@domain/EducationLevel";
import { HomophoneGroup } from "@domain/HomophoneGroup";
import { User } from "@domain/User";
import { provideRdxDialog, RdxDialogService } from "@radix-ng/primitives/dialog";
import { AnalyticsService } from "@services/analytics.service";
import { AuthenticationService } from "@services/authentication.service";
import { ErrorBoundaryService } from "@services/error-boundary.service";
import { HomophoneGroupService } from "@services/homophone-group.service";
import { MessagingService } from "@services/messaging.service";
import { RewardService } from "@services/reward.service";
import { SequenceService } from "@services/sequence.service";
import { AuthState } from "@states/auth.state";
import { filter } from "rxjs";

@Component({
  selector: "app-root",
  providers: [provideRdxDialog()],
  imports: [
    CommonModule,
    Exercises,
    WelcomeScreen,
    AuthenticatingScreen,
    RewardMechanism,
    EducationLevelSelection,
    HomophoneGroupSelection,
    Modal,
    SequenceEndScreen,
    ErrorPage,
  ],
  template: `
    @let isInitializing = authenticationService.isAuthenticating();
    @let isUserLoggedOut = authenticationService.userLoggedOut();
    @let showWelcomeScreen = !exerciserHasStarted() || isUserLoggedOut;
    @let educationLevelGroup = selectedEducationLevelGroup();
    @let homophoneGroups = selectedHomophoneGroups();
    @let hasError = errorBoundaryService.hasError();

    <app-modal />

    @if (hasError) {
      <app-error-page />
    } @else if (isInitializing) {
      <app-authenticating-screen />
    } @else {
      @if (this.rewardService.isRewardMechanismEnabled()) {
        <app-reward-mechanism [educationLevelGroup]="selectedEducationLevelGroup()" [showVisibilityToggle]="!showWelcomeScreen" />
      }

      @if (showWelcomeScreen) {
        <app-welcome-screen data-testid="welcome-screen" (exerciserHasStarted)="handleExerciserStart()" />
      } @else if (showResultsModal()) {
        <app-sequence-end-screen
          data-testid="sequence-end-screen"
          [educationLevelGroup]="selectedEducationLevelGroup()!"
          (quitExerciser)="handleQuitExerciserFromSequenceResults()"
          (startNewSequence)="handleNewSequenceStart()"
        />
      } @else if (!educationLevelGroup) {
        <app-education-level-selection
          data-testid="education-level-selection"
          (quitExerciser)="handleQuitExerciser()"
          (confirmEducationLevelSelection)="handleConfirmEducationLevelSelection($event)"
        />
      } @else if (!homophoneGroups) {
        <app-homophone-groups-selection
          data-testid="homophone-groups-selection"
          [selectedEducationLevelGroup]="educationLevelGroup"
          (quitExerciser)="handleQuitExerciser()"
          (confirmHomephoneGroupsSelection)="handleConfirmHomophoneGroupsSelection(educationLevelGroup, $event)"
        />
      } @else {
        <app-exercises
          [educationLevelGroup]="educationLevelGroup"
          [homophoneGroups]="homophoneGroups"
          (abortSequence)="handleAbortSequence()"
          (showSequenceCompletionModal)="handleShowSequenceCompletionModal()"
          (goToErrorReworkMode)="handleGoToErrorReworkMode()"
        />
      }
    }
  `,
})
export class App implements AfterViewInit {
  #modal = inject(RdxDialogService);

  protected authenticationService = inject(AuthenticationService);
  protected authState = inject(AuthState);
  protected errorBoundaryService = inject(ErrorBoundaryService);
  protected messagingService = inject(MessagingService);
  protected analyticsService = inject(AnalyticsService);
  protected rewardService = inject(RewardService);
  protected sequenceService = inject(SequenceService);
  protected homophoneGroupService = inject(HomophoneGroupService);

  protected exerciserHasStarted = signal(false);
  protected selectedEducationLevelGroup = signal<EducationLevelGroup | undefined>(undefined);
  protected selectedHomophoneGroups = signal<HomophoneGroup[] | undefined>(undefined);
  protected showResultsModal = signal(false);
  protected showAbortSequenceModal = signal(false);
  protected showSelectRewardCursorModal = signal(false);
  protected isFullScreen = signal(false);
  protected shouldShowRewardMechanism = signal(false);

  constructor() {
    toObservable<User | undefined>(this.authState.currentUser)
      .pipe(
        filter(user => !!user),
        takeUntilDestroyed()
      )
      .subscribe(currentUser => {
        if (!currentUser.hasAcknowledgedRewardCursor) {
          this.#modal.open({
            content: RewardCursorIndicationsModal,
            canCloseWithBackdrop: false,
          });
        }
      });

    toObservable<boolean>(this.sequenceService.isCurrentSequenceAborted)
      .pipe(
        filter(isCurrentSequenceAborted => !!isCurrentSequenceAborted),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.analyticsService.trackSequenceAbort(
          this.selectedEducationLevelGroup()!,
          this.selectedHomophoneGroups()!,
          this.sequenceService.currentExerciseNumber - 1
        );
        this.showResults();
      });

    toObservable<boolean>(this.sequenceService.isCurrentSequenceCompleted)
      .pipe(
        filter(isCurrentSequenceCompleted => !!isCurrentSequenceCompleted),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.showResults();
      });
  }

  async ngAfterViewInit() {
    window.addEventListener("message", event => this.messagingService.handleIframeMessageReception(event));
    this.messagingService.sendIframeReadyMessage();
  }

  handleConfirmEducationLevelSelection(educationLevelGroup: EducationLevelGroup): void {
    this.homophoneGroupService.selectEducationLevelGroup(educationLevelGroup);
    this.selectedEducationLevelGroup.set(educationLevelGroup);
  }

  handleConfirmHomophoneGroupsSelection(educationLevelGroup: EducationLevelGroup, homophoneGroups: HomophoneGroup[]) {
    this.analyticsService.trackSequenceStart(educationLevelGroup, homophoneGroups, false);
    this.selectedHomophoneGroups.set(homophoneGroups);
  }

  handleGoToErrorReworkMode() {
    const educationLevelGroup = this.selectedEducationLevelGroup();
    const homophoneGroups = this.selectedHomophoneGroups();

    if (!educationLevelGroup || !homophoneGroups) return;

    this.analyticsService.trackSequenceStart(educationLevelGroup, homophoneGroups, true);
  }

  handleCloseAbortSequenceModal(): void {
    this.showAbortSequenceModal.set(false);
  }

  handleAbortSequence(): void {
    if (this.sequenceService.hasAtLeastOneAnswer() || this.sequenceService.isInReworkErrorsMode()) {
      this.#modal.open({
        content: AbortSequenceModal,
        isAlert: true,
      });
      this.showAbortSequenceModal.set(true);
    } else {
      this.analyticsService.trackSequenceAbort(this.selectedEducationLevelGroup()!, this.selectedHomophoneGroups()!);
      this.returnToWelcomeScreen();
    }
  }

  handleShowSequenceCompletionModal() {
    this.#modal.open({
      content: SequenceCompletionModal,
      canCloseWithBackdrop: false,
      isAlert: true,
    });
  }

  showResults() {
    this.showAbortSequenceModal.set(false);
    this.showResultsModal.set(true);
  }

  handleNewSequenceStart() {
    this.analyticsService.trackNewSequenceStart(this.selectedEducationLevelGroup()!, this.selectedHomophoneGroups()!);
    this.showResultsModal.set(false);

    this.sequenceService.reset();
  }

  handleQuitExerciserFromSequenceResults() {
    this.analyticsService.trackExerciseEnd(this.selectedEducationLevelGroup()!, this.selectedHomophoneGroups()!);
    this.showResultsModal.set(false);
    this.returnToWelcomeScreen();
  }

  handleQuitExerciser() {
    this.returnToWelcomeScreen();
  }

  private returnToWelcomeScreen() {
    this.selectedEducationLevelGroup.set(undefined);
    this.selectedHomophoneGroups.set(undefined);
    this.sequenceService.reset();
    this.exerciserHasStarted.set(false);
  }

  handleExerciserStart() {
    this.homophoneGroupService.fetchHomophoneGroups();
    this.exerciserHasStarted.set(true);
  }
}
